import { type TPaymentMethods, type THeapPaymentMethodsJson } from "@features/checkout/types";
import type { PaymentMethodResponse } from "@interfaces/heap-api";
import { ClientFetchService } from "@lib/helpers/clientFetchService";

export type HeapAdyenPaymentMethods = {
    paymentMethods: TPaymentMethods[];
    /** The key checkout uses too; Adyen rejects one attempt ID sent with two keys. */
    clientKey?: string;
};

const isAdyenProvider = (provider: unknown): provider is THeapPaymentMethodsJson =>
    typeof provider === "object" &&
    provider !== null &&
    (provider as { PaymentProviderType?: unknown }).PaymentProviderType === "Adyen";

/**
 * Fetches Adyen payment methods from HEAP for surfaces that only display available
 * methods (payment icons, express pay). The cartId is required: without it HEAP has no
 * amount, and markets with Amazon Pay fail the entire response while building its payload.
 * Returns no methods for malformed or unexpected payloads so callers always get a safe array.
 */
export const fetchHeapAdyenPaymentMethods = async (
    cartId: string
): Promise<HeapAdyenPaymentMethods> => {
    const response = await ClientFetchService.post<{ cartId: string }, PaymentMethodResponse>(
        "/api/payment/heap/get-methods",
        { cartId }
    );

    const clientKey = response?.adyenClientKey ?? undefined;

    let parsed: unknown;
    try {
        parsed = JSON.parse(response?.paymentMethodsJson ?? "[]");
    } catch {
        return { paymentMethods: [], clientKey };
    }

    if (!Array.isArray(parsed)) {
        return { paymentMethods: [], clientKey };
    }

    const adyenProvider = parsed.find(isAdyenProvider);

    const paymentMethods =
        adyenProvider?.PaymentProviderType === "Adyen" &&
        Array.isArray(adyenProvider.paymentMethods)
            ? adyenProvider.paymentMethods
            : [];

    return { paymentMethods, clientKey };
};
